import {
  ReactNode, RefObject, useCallback, useEffect, useState,
} from 'react';
import { createPortal } from 'react-dom';
import S from './HoverCaptionStyles';

const CAPTION_GAP = 6;
const MIN_SPACE_ABOVE = 48;

type Placement = {
  top: number;
  left: number;
  below: boolean;
  alignRight: boolean;
};

type Props = {
  anchorRef: RefObject<HTMLElement>;
  children: ReactNode;
};

function HoverCaption({ anchorRef, children }: Props): JSX.Element | null {
  const [placement, setPlacement] = useState<Placement | null>(null);

  const updatePlacement = useCallback((): void => {
    const anchor = anchorRef.current;
    if (!anchor) {
      setPlacement(null);
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const below = rect.top < MIN_SPACE_ABOVE;
    const alignRight = rect.left > window.innerWidth / 2;

    setPlacement({
      top: below ? rect.bottom + CAPTION_GAP : rect.top - CAPTION_GAP,
      left: alignRight ? rect.right : rect.left,
      below,
      alignRight,
    });
  }, [anchorRef]);

  useEffect(() => {
    updatePlacement();
    window.addEventListener('scroll', updatePlacement, true);
    window.addEventListener('resize', updatePlacement);

    return () => {
      window.removeEventListener('scroll', updatePlacement, true);
      window.removeEventListener('resize', updatePlacement);
    };
  }, [updatePlacement]);

  if (!placement) {
    return null;
  }

  return createPortal(
    <S.Bubble
      $below={placement.below}
      $alignRight={placement.alignRight}
      style={{ top: placement.top, left: placement.left }}
    >
      {children}
    </S.Bubble>,
    document.body,
  );
}

export default HoverCaption;
